import type {
  Estado,
  NovaDemanda as NovaDemandaDados,
  Prioridade,
  TipoDemanda,
} from '@contefy/shared';
import {
  ESTADO_NOME,
  ESTADOS,
  PRIORIDADE_INFO,
  PRIORIDADES,
  TIPO_DEMANDA_INFO,
  TIPOS_DEMANDA,
} from '@contefy/shared';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Alerta, Botao } from '../componentes/ui';
import { equipeApi } from './api';

/**
 * Cadastro manual de uma demanda do Societário (cliente que pediu direto, sem passar pelo site).
 * O cartão entra na coluna de triagem; a equipe move dali em diante.
 */
export function NovaDemanda({ aoFechar }: { aoFechar: () => void }) {
  const navegar = useNavigate();
  const [tipo, setTipo] = useState<TipoDemanda>('abertura');
  const [estado, setEstado] = useState<Estado>('PR');
  const [tipoEmpresa, setTipoEmpresa] = useState<'EI' | 'LTDA'>('LTDA');
  const [prioridade, setPrioridade] = useState<Prioridade>('media');
  const [nome, setNome] = useState('');
  const [telefone, setTelefone] = useState('');
  const [email, setEmail] = useState('');
  const [empresa, setEmpresa] = useState('');
  const [municipio, setMunicipio] = useState('');
  const [descricao, setDescricao] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const salvar = async () => {
    setSalvando(true);
    setErro(null);
    try {
      const dados: NovaDemandaDados = {
        tipo_demanda: tipo,
        estado,
        tipo_empresa: tipoEmpresa,
        nome_contato: nome.trim(),
        telefone_contato: telefone.trim(),
        email_contato: email.trim() || null,
        nome_empresa: empresa.trim() || null,
        municipio: municipio.trim() || null,
        descricao: descricao.trim() || null,
        prioridade,
      };
      const r = await equipeApi.criarDemanda(dados);
      aoFechar();
      navegar(`/equipe/aberturas/${r.id}`);
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setSalvando(false);
    }
  };

  const pronto = nome.trim().length >= 2 && telefone.trim().length >= 8;

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-slate-900/50 p-4">
      <div className="mt-8 w-full max-w-2xl rounded-2xl bg-white p-5 shadow-xl">
        <div className="mb-3">
          <h2 className="text-lg font-semibold text-slate-900">Nova demanda</h2>
          <p className="text-sm text-slate-600">
            Para pedidos que chegam direto do cliente. O cartão entra em "Triagem / Análise de
            dados" e você move a partir dali. Nada é enviado ao cliente.
          </p>
        </div>
        {erro && <Alerta tipo="erro">{erro}</Alerta>}
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-sm">
            <span className="text-slate-600">Tipo de demanda</span>
            <select
              className="mt-1 w-full"
              value={tipo}
              onChange={(e) => setTipo(e.target.value as TipoDemanda)}
            >
              {TIPOS_DEMANDA.map((t) => (
                <option key={t} value={t}>
                  {TIPO_DEMANDA_INFO[t].titulo}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <span className="text-slate-600">Prioridade</span>
            <select
              className="mt-1 w-full"
              value={prioridade}
              onChange={(e) => setPrioridade(e.target.value as Prioridade)}
            >
              {PRIORIDADES.map((p) => (
                <option key={p} value={p}>
                  {PRIORIDADE_INFO[p].titulo}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <span className="text-slate-600">Estado (Junta)</span>
            <select
              className="mt-1 w-full"
              value={estado}
              onChange={(e) => setEstado(e.target.value as Estado)}
            >
              {ESTADOS.map((uf) => (
                <option key={uf} value={uf}>
                  {ESTADO_NOME[uf]}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <span className="text-slate-600">Tipo da empresa</span>
            <select
              className="mt-1 w-full"
              value={tipoEmpresa}
              onChange={(e) => setTipoEmpresa(e.target.value as 'EI' | 'LTDA')}
            >
              <option value="LTDA">Sociedade Limitada</option>
              <option value="EI">Empresário Individual</option>
            </select>
          </label>
          <label className="text-sm">
            <span className="text-slate-600">Nome do cliente (contato)</span>
            <input className="mt-1 w-full" value={nome} onChange={(e) => setNome(e.target.value)} />
          </label>
          <label className="text-sm">
            <span className="text-slate-600">WhatsApp</span>
            <input
              className="mt-1 w-full"
              value={telefone}
              onChange={(e) => setTelefone(e.target.value)}
              placeholder="(41) 99999-9999"
            />
          </label>
          <label className="text-sm">
            <span className="text-slate-600">E-mail (opcional)</span>
            <input
              className="mt-1 w-full"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <label className="text-sm">
            <span className="text-slate-600">Empresa</span>
            <input
              className="mt-1 w-full"
              value={empresa}
              onChange={(e) => setEmpresa(e.target.value)}
              placeholder="Nome da empresa ou pretendido"
            />
          </label>
          <label className="text-sm sm:col-span-2">
            <span className="text-slate-600">Município</span>
            <input
              className="mt-1 w-full"
              value={municipio}
              onChange={(e) => setMunicipio(e.target.value)}
            />
          </label>
          <label className="text-sm sm:col-span-2">
            <span className="text-slate-600">O que o cliente pediu</span>
            <textarea
              className="mt-1 min-h-24 w-full rounded-lg border border-slate-300 p-2"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              placeholder="Ex.: alteração de endereço e inclusão de CNAE; cliente quer até o fim do mês."
            />
          </label>
        </div>
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <Botao variante="secundario" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao carregando={salvando} disabled={!pronto} onClick={salvar}>
            Criar demanda
          </Botao>
        </div>
      </div>
    </div>
  );
}
